/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  AiScanTargetGatewayClient,
  AiScanTargetGatewayClient$inboundSchema,
} from "./aiscantargetgatewayclient.js";
import {
  AiScanTargetSignatures,
  AiScanTargetSignatures$inboundSchema,
} from "./aiscantargetsignatures.js";
import {
  AIToolAccessSummary,
  AIToolAccessSummary$inboundSchema,
} from "./aitoolaccesssummary.js";

/**
 * Target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally).
 */
export const AiScanTargetCategory = {
  Harness: "harness",
  Assistant: "assistant",
  LocalModel: "local_model",
} as const;
/**
 * Target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally).
 */
export type AiScanTargetCategory = OpenEnum<typeof AiScanTargetCategory>;

/**
 * Where the target comes from: default (a Speakeasy built-in, whose definition is read-only) or organization (added by the organization, fully editable). Every target listed here is probed for; a built-in leaves the list by being removed from Speakeasy's catalog, an organization target by being deleted.
 */
export const Origin = {
  Default: "default",
  Organization: "organization",
} as const;
/**
 * Where the target comes from: default (a Speakeasy built-in, whose definition is read-only) or organization (added by the organization, fully editable). Every target listed here is probed for; a built-in leaves the list by being removed from Speakeasy's catalog, an organization target by being deleted.
 */
export type Origin = OpenEnum<typeof Origin>;

/**
 * One Shadow AI scan target in an organization's list: a Speakeasy built-in, or a target the organization added.
 */
export type AiScanTarget = {
  /**
   * The enforcement verdict for one AI tool, computed server-side so a client renders wording without re-deriving it. state shares its vocabulary with the shadow MCP inventory's access summary, so one column describes both halves of the Shadow AI section.
   */
  access?: AIToolAccessSummary | undefined;
  /**
   * Target category: harness (an AI coding tool), assistant (a general-purpose AI assistant or agent), or local_model (an open model run locally).
   */
  category: AiScanTargetCategory;
  /**
   * When the organization's row was created; absent for an untouched default.
   */
  createdAt?: Date | undefined;
  /**
   * For a built-in, whether the organization has recorded an access decision about it. Always false for organization targets.
   */
  customized: boolean;
  /**
   * Name shown in the dashboard.
   */
  displayName: string;
  /**
   * How a target detected on a device is recognized again when the same tool calls Speakeasy's MCP gateway. A device signature and a registered OAuth client share no natural join key, so the link is declared here. The three lists are not interchangeable: the first two name credentials Speakeasy verified and can be enforced on, the third names what a client said about itself and is used only to attribute traffic.
   */
  gatewayClient: AiScanTargetGatewayClient;
  /**
   * Stable id agents report and detections key on.
   */
  id: string;
  /**
   * Where the target comes from: default (a Speakeasy built-in, whose definition is read-only) or organization (added by the organization, fully editable). Every target listed here is probed for; a built-in leaves the list by being removed from Speakeasy's catalog, an organization target by being deleted.
   */
  origin: Origin;
  /**
   * On-device signals the scan checks for one target.
   */
  signatures: AiScanTargetSignatures;
  /**
   * When the organization's row last changed; absent for an untouched default.
   */
  updatedAt?: Date | undefined;
  /**
   * Info.plist key the installed version is read from on a bundle match; defaults to CFBundleShortVersionString when omitted.
   */
  versionPlistKey?: string | undefined;
};

/** @internal */
export const AiScanTargetCategory$inboundSchema: z.ZodMiniType<
  AiScanTargetCategory,
  unknown
> = openEnums.inboundSchema(AiScanTargetCategory);

/** @internal */
export const Origin$inboundSchema: z.ZodMiniType<Origin, unknown> = openEnums
  .inboundSchema(Origin);

/** @internal */
export const AiScanTarget$inboundSchema: z.ZodMiniType<AiScanTarget, unknown> =
  z.pipe(
    z.object({
      access: z.optional(AIToolAccessSummary$inboundSchema),
      category: AiScanTargetCategory$inboundSchema,
      created_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      customized: z.boolean(),
      display_name: z.string(),
      gateway_client: AiScanTargetGatewayClient$inboundSchema,
      id: z.string(),
      origin: Origin$inboundSchema,
      signatures: AiScanTargetSignatures$inboundSchema,
      updated_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      version_plist_key: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "created_at": "createdAt",
        "display_name": "displayName",
        "gateway_client": "gatewayClient",
        "updated_at": "updatedAt",
        "version_plist_key": "versionPlistKey",
      });
    }),
  );

export function aiScanTargetFromJSON(
  jsonString: string,
): SafeParseResult<AiScanTarget, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AiScanTarget$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AiScanTarget' from JSON`,
  );
}
